import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

import type { CardType } from './KanbanBoard';

interface CardProps {
  card: CardType;
  isStep?: boolean;
  checked?: boolean;
  note?: string;
  onCheck?: (checked: boolean) => void;
  onNoteChange?: (note: string) => void;
}

export const Card: React.FC<CardProps> = ({ card, isStep, checked, note, onCheck, onNoteChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  // Helper for copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Render tags
  const renderTags = () => (
    <div className="flex flex-wrap gap-1 mt-1">
      {card.tags?.map(tag => (
        <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">#{tag}</span>
      ))}
    </div>
  );

  // Methodology step card
  if (isStep) {
    return (
      <div className="bg-white rounded shadow p-2 mb-2 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <button className="btn btn-xs" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
          <input type="checkbox" checked={checked} onChange={e => onCheck?.(e.target.checked)} />
          <span className="font-semibold">{card.title}</span>
        </div>
        {!collapsed && <>
          <ReactMarkdown>{card.content}</ReactMarkdown>
          <textarea
            className="border rounded p-1 text-sm mt-1"
            placeholder="Add a note..."
            value={note || ''}
            onChange={e => onNoteChange?.(e.target.value)}
          />
          {renderTags()}
        </>}
      </div>
    );
  }

  // Render by card type
  switch (card.type) {
    case 'note':
      return (
        <div className="bg-white rounded shadow p-2 mb-2">
          <div className="flex items-center mb-1">
            <button className="btn btn-xs mr-2" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
            <span className="font-semibold">📝 {card.title}</span>
          </div>
          {!collapsed && <>
            <ReactMarkdown>{card.content}</ReactMarkdown>
            {renderTags()}
          </>}
        </div>
      );
    case 'resource':
      return (
        <div className="bg-white rounded shadow p-2 mb-2">
          <div className="flex items-center mb-1">
            <button className="btn btn-xs mr-2" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
            <span className="font-semibold">🔗 {card.title}</span>
          </div>
          {!collapsed && <>
            <a href={card.content.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Open Resource</a>
            {renderTags()}
          </>}
        </div>
      );
    case 'vuln':
      return (
        <div className="bg-white rounded shadow p-2 mb-2 border-l-4" style={{ borderColor: getSeverityColor(card.content.severity) }}>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center">
              <button className="btn btn-xs mr-2" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
              <span className="font-semibold">🐞 {card.title}</span>
            </div>
            <span className="px-2 py-0.5 rounded text-xs" style={{ background: getSeverityColor(card.content.severity), color: 'white' }}>{card.content.severity}</span>
          </div>
          {!collapsed && <>
            <div className="text-xs mb-1">Endpoint: <span className="font-mono">{card.content.endpoint}</span></div>
            <div className="text-xs mb-1">Status: <span>{card.content.status}</span></div>
            <div className="mb-1"><ReactMarkdown>{card.content.description}</ReactMarkdown></div>
            <div className="mb-1"><b>PoC:</b> <ReactMarkdown>{card.content.poc}</ReactMarkdown></div>
            {renderTags()}
          </>}
        </div>
      );
    case 'tool':
      return (
        <div className="bg-white rounded shadow p-2 mb-2">
          <div className="flex items-center mb-1">
            <button className="btn btn-xs mr-2" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
            <span className="font-semibold">🛠️ {card.title}</span>
          </div>
          {!collapsed && <>
            <div className="flex items-center mb-1">
              <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm">{card.content.command}</code>
              <button className="ml-2 btn btn-xs" onClick={() => handleCopy(card.content.command)}>Copy</button>
            </div>
            {renderTags()}
          </>}
        </div>
      );
    case 'payload':
      return (
        <div className="bg-white rounded shadow p-2 mb-2">
          <div className="flex items-center mb-1">
            <button className="btn btn-xs mr-2" onClick={() => setCollapsed(c => !c)}>{collapsed ? '+' : '-'}</button>
            <span className="font-semibold">💾 {card.title}</span>
          </div>
          {!collapsed && <>
            <div className="flex items-center mb-1">
              <pre className="bg-gray-100 px-2 py-1 rounded font-mono text-sm overflow-x-auto">{card.content.code}</pre>
              <button className="ml-2 btn btn-xs" onClick={() => handleCopy(card.content.code)}>Copy</button>
            </div>
            {renderTags()}
          </>}
        </div>
      );
    default:
      return null;
  }
};

function getSeverityColor(severity: string) {
  switch (severity) {
    case 'Critical': return '#e53e3e';
    case 'High': return '#dd6b20';
    case 'Medium': return '#ecc94b';
    case 'Low': return '#38a169';
    case 'Info': return '#3182ce';
    default: return '#a0aec0';
  }
} 